import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { bankApi, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2 } from "lucide-react";

export default function FdicHoldDialog({
  open,
  onOpenChange,
  transaction,
  fee,
  balances,
  onPaid,
}) {
  const [payFrom, setPayFrom] = useState("checking");
  const [paying, setPaying] = useState(false);

  if (!transaction) return null;

  const insufficient = balances && balances[payFrom] < fee;

  const handlePay = async () => {
    if (insufficient) {
      toast.error(`Insufficient funds in ${payFrom} to cover the FDIC fee`);
      return;
    }
    setPaying(true);
    try {
      await new Promise((r) => setTimeout(r, 1000));
      const res = await bankApi.payFee(transaction.id, payFrom);
      toast.success(
        `FDIC hold released. Fee of ${formatMoney(res.fee_paid)} paid.`
      );
      onPaid && onPaid(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fee payment failed");
    } finally {
      setPaying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="fdic-hold-dialog"
        className="rounded-none border-federal-navy max-w-lg p-0"
      >
        <div className="bg-federal-navy text-white p-6 grain-overlay">
          <div className="flex items-center gap-3 mb-1">
            <ShieldAlert
              className="w-5 h-5 text-institutional-gold"
              strokeWidth={1.5}
            />
            <span className="uppercase tracking-[0.25em] text-[10px] text-institutional-gold font-semibold">
              FDIC Hold Notice
            </span>
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="font-serif text-3xl text-white">
              Insurance fee required
            </DialogTitle>
            <DialogDescription className="text-white/70 mt-2">
              Your wire has been placed on FDIC hold. Federal deposit
              insurance requires a one-time fee before the funds are
              released to the beneficiary.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-6 bg-white">
          <div className="border border-slate-200 divide-y divide-slate-200 text-sm">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-slate-500 uppercase tracking-widest text-[10px]">
                Beneficiary
              </span>
              <span className="text-federal-navy font-medium">
                {transaction.recipient_name}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-slate-500 uppercase tracking-widest text-[10px]">
                Transfer amount
              </span>
              <span className="text-federal-navy tabular-nums font-mono">
                {formatMoney(transaction.amount)}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-[#FCF7EA]">
              <span className="text-federal-navy uppercase tracking-widest text-[10px] font-semibold">
                FDIC Insurance Fee
              </span>
              <span
                className="text-federal-navy tabular-nums font-mono text-lg"
                data-testid="fdic-fee-amount"
              >
                {formatMoney(fee)}
              </span>
            </div>
            <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-slate-500 flex justify-between">
              <span>Starting price</span>
              <span className="font-mono">$9,500.00 minimum · 1% of wire</span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-slate-700">
              Deduct fee from
            </div>
            <Select value={payFrom} onValueChange={setPayFrom}>
              <SelectTrigger
                data-testid="fee-from-account-select"
                className="rounded-none border-slate-300 h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="checking">
                  Checking · {balances ? formatMoney(balances.checking) : "—"}
                </SelectItem>
                <SelectItem value="savings">
                  Savings · {balances ? formatMoney(balances.savings) : "—"}
                </SelectItem>
                <SelectItem value="escrow">
                  Escrow · {balances ? formatMoney(balances.escrow) : "—"}
                </SelectItem>
              </SelectContent>
            </Select>
            {insufficient && (
              <div className="text-xs text-[#800000] mt-1">
                Insufficient funds in {payFrom} for the FDIC fee.
              </div>
            )}
          </div>

          <p className="text-xs text-slate-500 italic leading-relaxed">
            Federal deposit insurance is administered as a one-time premium at
            the point of transfer. Funds remain in the reserve pool until the
            fee is settled.
          </p>
        </div>

        <DialogFooter className="p-6 pt-0 bg-white gap-3 sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="fdic-hold-later-btn"
            className="rounded-none border-slate-300 uppercase tracking-widest text-xs"
          >
            Pay later
          </Button>
          <Button
            onClick={handlePay}
            disabled={paying || insufficient}
            data-testid="fdic-hold-pay-btn"
            className="rounded-none bg-federal-navy hover:bg-federal-navy/90 text-white uppercase tracking-widest text-xs disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {paying ? "Processing…" : `Pay ${formatMoney(fee)} & release`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
